import { ARENA } from '../../config/constants.js';

const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Anglerling (Act II, ACT2-BIBLE.md §8) — a Gloam echo: you only see it once
 * it's close.
 *
 * Its body is drawn only within `revealRadius` of the player (Game._drawMinions
 * reads `revealed`); outside that, all you get is the lure — a single mote
 * drawn at its position. So it is never *invisible*, which would be unfair;
 * it's a light in the dark that turns out to be attached to something, which
 * is the same lesson the Gloam teaches at boss scale. That makes it honest in
 * a lit arena too: the lure is always where the Anglerling is.
 *
 * Fast and fragile, so the tension is "that mote is closer than it was" rather
 * than a damage race.
 */
export class Anglerling {
  constructor(eventBus, { x, y, radius = 4, hp, moveSpeed, contactDamage, revealRadius = 58 }) {
    this.eventBus = eventBus;
    this.type = 'anglerling';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.revealRadius = revealRadius;
    // Read by Game._drawMinions: false = draw the lure mote only.
    this.revealed = false;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
    // Phase offset so a wave of them doesn't pulse their lures in lockstep.
    this.lurePhase = Math.random() * Math.PI * 2;
  }

  update(dt, player) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;
    this._clampToArena();

    this.revealed = dist <= this.revealRadius;

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    this.hitFlashTimer = HIT_FLASH_DURATION;
    // Being hit lights it up regardless of distance — a shot that connects
    // must tell you what it connected with, or the type is just noise.
    this.revealed = true;
    this.eventBus.emit('minion:damaged', { amount: hpBefore - this.hp, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
